import React from "react"

import { IconButton, Tooltip, Typography } from "@mui/material"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { AddIcon, HelpOutlineRoundedIcon } from "@l2r-front/l2r-icons"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { CatalogListItem } from "../CatalogListItem"
import { L2RButton } from "../L2RButton"
import { L2RLink } from "../L2RLink"
import { MoreMenuItemConfig } from "../MoreMenu"

import * as Styled from "./CatalogList.styled"

type CatalogListProps = {
    alwaysDisplayIconsWhenAvailable?: boolean,
    ascendantCodes?: string[],
    baseItemCode?: string,
    className?: string,
    data: Array<{
        code: string,
        name: string,
        icon?: string,
        disabled?: boolean,
        tooltipText?: string,
        children: Array<{
            code: string,
            name: string,
            icon?: string,
            disabled?: boolean,
            tooltipText?: string,
        }>,
    }>,
    defaultIcon?: string,
    emptyListComponent?: React.JSX.Element,
    emptyListText?: string,
    filter?: string | null,
    showFilteredCount?: boolean,
    ItemsCountComponent?: (_props: { signType?: string, isMainCount?: boolean, displayTotalCount?: boolean }) => React.JSX.Element,
    ItemsCountComponentProps?: any,
    moreMenuConfigs?: { [key: string]: MoreMenuItemConfig[] },
    onClick: (_code: string) => void,
    onCreate?: () => void,
    title: string,
    titleNavigationUrl?: string,
    tooltipText?: string,
}

export function CatalogList(props: CatalogListProps) {

    const {
        alwaysDisplayIconsWhenAvailable = false,
        ascendantCodes,
        baseItemCode,
        defaultIcon = "",
        className,
        data,
        emptyListComponent,
        emptyListText,
        filter,
        ItemsCountComponent,
        ItemsCountComponentProps,
        moreMenuConfigs,
        onCreate,
        onClick,
        title,
        titleNavigationUrl,
        tooltipText,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    return <div className={className}>
        <Styled.Header>
            <Styled.TitleContainer>
                {titleNavigationUrl ? <L2RLink color="inherit" to={titleNavigationUrl} underline="none">
                    <Styled.LinkText variant="Desktop & Mobile/Heading/H2" underline={!!titleNavigationUrl}>
                        {title}
                    </Styled.LinkText>
                </L2RLink> : <Typography variant="Desktop & Mobile/Heading/H2">
                    {title}
                </Typography>}
                {!!tooltipText && <Tooltip disableFocusListener title={tooltipText}>
                    <IconButton size="small">
                        <HelpOutlineRoundedIcon color="action/disabled" />
                    </IconButton>
                </Tooltip>}
            </Styled.TitleContainer>
            <Styled.ButtonsContainer>
                {ItemsCountComponent && <ItemsCountComponent signType={baseItemCode} isMainCount {...ItemsCountComponentProps} />}
                {onCreate && <L2RButton
                    shape="circle"
                    size="small"
                    onClick={onCreate}>
                    <AddIcon />
                </L2RButton>}
            </Styled.ButtonsContainer>
        </Styled.Header>
        {data?.length ? <Styled.List>
            {
                data.map((dataItem) => {
                    const children = dataItem?.children?.length ? dataItem.children.map(child => {
                        return <CatalogListItem
                            disabled={child.disabled}
                            icon={getIcon(child, defaultIcon, alwaysDisplayIconsWhenAvailable)}
                            key={child.code}
                            defaultIcon={defaultIcon}
                            expandable={false}
                            highlighted={filter !== child.code && (ascendantCodes || []).includes(child.code)}
                            label={child.name}
                            moreMenuConfig={moreMenuConfigs?.[child.code]}
                            onClick={() => onClick(child.code)}
                            selected={filter === child.code}
                            ItemsCountComponent={ItemsCountComponent && <ItemsCountComponent signType={child.code} {...ItemsCountComponentProps} />}
                            tooltipText={child.tooltipText}
                        />
                    })
                        : null

                    return <CatalogListItem
                        disabled={dataItem.disabled}
                        icon={getIcon(dataItem, defaultIcon, alwaysDisplayIconsWhenAvailable)}
                        defaultIcon={defaultIcon}
                        key={dataItem.code}
                        expandable={!!children}
                        children={children}
                        highlighted={filter !== dataItem.code && (ascendantCodes || []).includes(dataItem.code)}
                        label={dataItem.name}
                        moreMenuConfig={moreMenuConfigs?.[dataItem.code]}
                        onClick={() => onClick(dataItem.code)}
                        selected={filter === dataItem.code}
                        startOpen={children && (ascendantCodes || []).includes(dataItem.code)}
                        ItemsCountComponent={ItemsCountComponent && <ItemsCountComponent
                            displayTotalCount={!!dataItem?.children?.length}
                            signType={dataItem.code}
                            {...ItemsCountComponentProps}
                        />}
                        tooltipText={dataItem.tooltipText}
                    />
                })
            }
        </Styled.List>
            : emptyListComponent ? emptyListComponent : <Typography>
                {emptyListText ? emptyListText : t(I18N_NAMESPACE, "components.catalogList.emptyList")}
            </Typography>}
    </div>
}

function getIcon(item: CatalogListItem, defaultIcon: string, alwaysDisplayIconsWhenAvailable: boolean) {
    const shouldHideIcon = alwaysDisplayIconsWhenAvailable ? !item.icon : item?.children?.length
    if (shouldHideIcon) {
        return null
    }
    return item?.icon ?? defaultIcon
}

type CatalogListItem = {
    code: string,
    name: string,
    icon?: string,
    disabled?: boolean,
    tooltipText?: string,
    children?: CatalogListItem[],
}
